import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Books, Sparkle, Brain, ShieldCheck, ShieldWarning, Globe, ArrowsClockwise,
  Plus, X, ChatTeardropDots, GraduationCap, Quotes,
} from "@phosphor-icons/react";

/* ────────────────────────────────────────────────────────────────────────────
 * Retrospectives · Fused Retrospective UI
 *   LEFT pane : Professional Insight form (Strategy / Supplier / Market Correction)
 *   RIGHT pane: Agentic Validation — Strategic Partner (Lead) cross-reference against
 *               FRED / BLS / GDELT, returned as a Shared Victory Ledger.
 * ────────────────────────────────────────────────────────────────────────── */

const SCOPE_TYPES = [
  { value: "general",  label: "Portfolio" },
  { value: "supplier", label: "Supplier" },
  { value: "segment",  label: "UNSPSC Segment" },
  { value: "agency",   label: "Agency" },
  { value: "po",       label: "Purchase Order" },
];

const ALIGNMENT_TONE = {
  CORROBORATED: { fg: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40" },
  PARTIAL:      { fg: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-500/40" },
  CONTRADICTED: { fg: "text-rose-300",    bg: "bg-rose-500/10",    border: "border-rose-500/40" },
};

const SOURCE_FG = { FRED: "text-amber-300", BLS: "text-sky-300", GDELT: "text-fuchsia-300", ECB: "text-violet-300", Procureo: "text-emerald-300" };

export default function RetrospectivesView() {
  const [list, setList]   = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const refresh = () =>
    api.retrospectives().then((d) => setList(d.retrospectives || [])).catch(() => setList([]));

  useEffect(() => { refresh(); }, []);

  const active = useMemo(
    () => (list || []).find((r) => r.id === activeId) || null,
    [list, activeId],
  );

  if (!list) {
    return <div data-testid="retro-loading" className="h-[480px] skeleton" />;
  }

  return (
    <section data-testid="retrospectives-view" className="bg-slate-900 border border-slate-800">
      <Header onAdd={() => { setShowForm(true); setActiveId(null); }} count={list.length} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-px bg-slate-800">
        {/* List rail */}
        <aside className="xl:col-span-3 bg-slate-950 p-4 min-h-[520px]">
          <div className="flex items-center justify-between mb-2">
            <span className="tracking-mono-label text-[10px] text-slate-500">Retrospectives</span>
            <span className="font-mono text-[10px] text-slate-500">{list.length}</span>
          </div>
          <ul className="space-y-1.5 max-h-[480px] overflow-auto thin-scroll" data-testid="retro-list">
            {list.map((r) => {
              const tone = ALIGNMENT_TONE[r.validation?.alignment] || ALIGNMENT_TONE.PARTIAL;
              return (
                <li key={r.id}>
                  <button
                    data-testid={`retro-row-${r.id}`}
                    onClick={() => { setActiveId(r.id); setShowForm(false); }}
                    className={`w-full text-left p-2.5 rounded-sm border transition-colors ${
                      activeId === r.id
                        ? "border-emerald-500/50 bg-emerald-500/10"
                        : "border-slate-800 hover:border-slate-700 hover:bg-slate-900"
                    }`}
                  >
                    <div className="text-xs text-slate-100 truncate">{r.title}</div>
                    <div className="font-mono text-[10px] text-slate-500 truncate">
                      {r.author} · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                    <div className={`font-mono text-[10px] mt-0.5 ${tone.fg}`}>
                      {r.validation?.alignment || "PARTIAL"} · {Math.round((r.validation?.confidence || 0) * 100)}% conf
                    </div>
                  </button>
                </li>
              );
            })}
            {list.length === 0 && (
              <li className="text-[11px] font-mono text-slate-500 text-center py-6">
                No retrospectives yet. Add the first to teach the Strategic Partner.
              </li>
            )}
          </ul>
        </aside>

        {/* Main pane */}
        <main className="xl:col-span-9 bg-slate-950 min-h-[520px]">
          {showForm
            ? <ProfessionalInputForm
                onSubmitted={(r) => { refresh().then(() => setActiveId(r.id)); setShowForm(false); }}
                onCancel={() => setShowForm(false)}
              />
            : active
              ? <FusedRetrospective retro={active} />
              : <EmptyState onAdd={() => setShowForm(true)} />}
        </main>
      </div>
    </section>
  );
}

function Header({ onAdd, count }) {
  return (
    <header className="px-5 py-4 border-b border-slate-800 flex items-baseline justify-between gap-4 flex-wrap">
      <div>
        <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
          <Books weight="duotone" className="text-emerald-400" size={16} />
          Retrospectives · Shared Victory Ledger
        </h2>
        <p className="text-xs text-slate-500 mt-0.5 font-mono">
          Professional intuition meets Strategic Partner cross-reference against FRED · BLS · GDELT — durable institutional wisdom.
        </p>
      </div>
      <button
        data-testid="add-retrospective"
        onClick={onAdd}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                   text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20 transition-colors"
      >
        <Plus weight="bold" size={12} /> add retrospective
      </button>
    </header>
  );
}

/* ─── Form (LEFT pane during creation) ─────────────────────────────────── */
function ProfessionalInputForm({ onSubmitted, onCancel }) {
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("Cameron · Category Lead");
  const [scope, setScope] = useState({ type: "general", value: "", label: "Portfolio-wide" });
  const [notes, setNotes] = useState({ strategy_effectiveness: "", supplier_behavior: "", market_correction: "", free_form: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { setError("Give the retrospective a title."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.createRetrospective({ title, author, scope, notes });
      onSubmitted?.(r);
    } catch (e) {
      setError(e?.message || "Cross-reference failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="p-5 space-y-4" data-testid="retro-form">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-display text-sm font-semibold text-slate-50 flex items-center gap-2">
            <Sparkle weight="duotone" className="text-emerald-400" size={14} />
            Professional Insight · LEFT pane
          </h3>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            Capture what you observed. Strategic Partner will cross-reference your prose against FRED · BLS · GDELT and return the validation alongside.
          </p>
        </div>
        <button type="button" onClick={onCancel}
          className="font-mono text-[10px] tracking-mono-label text-slate-400 hover:text-slate-200">
          ✕ cancel
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input testid="retro-title" label="Title" value={title} onChange={setTitle}
               placeholder="e.g. Q3 Bechtel hedge held against the Copper rally" />
        <Input testid="retro-author" label="Author" value={author} onChange={setAuthor} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Select testid="retro-scope-type" label="Scope" value={scope.type}
                onChange={(v) => setScope({ ...scope, type: v })}
                options={SCOPE_TYPES} />
        <Input testid="retro-scope-value" label="Scope value (optional)"
               value={scope.value} onChange={(v) => setScope({ ...scope, value: v })}
               placeholder="e.g. supplier ID, segment code 30000000" />
        <Input testid="retro-scope-label" label="Scope label (optional)"
               value={scope.label} onChange={(v) => setScope({ ...scope, label: v })}
               placeholder="e.g. Bechtel · DOD Pacific" />
      </div>

      <Textarea testid="retro-strategy" label="Strategy effectiveness"
        placeholder="What strategy did you run? Did it deliver against the OODA hypothesis?"
        rows={3} value={notes.strategy_effectiveness}
        onChange={(v) => setNotes({ ...notes, strategy_effectiveness: v })} />

      <Textarea testid="retro-supplier" label="Supplier behavior"
        placeholder="How did the counterparty actually behave? Trustworthy? Tactical? Distressed?"
        rows={3} value={notes.supplier_behavior}
        onChange={(v) => setNotes({ ...notes, supplier_behavior: v })} />

      <Textarea testid="retro-market" label="Market correction observed"
        placeholder="What macro move corrected your assumption? Tie it to a series (FRED, BLS, ECB) if you can."
        rows={3} value={notes.market_correction}
        onChange={(v) => setNotes({ ...notes, market_correction: v })} />

      {error && <div className="text-rose-300 text-xs font-mono" data-testid="retro-error">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          data-testid="submit-retro"
          type="submit" disabled={submitting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                     text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20 disabled:opacity-50"
        >
          {submitting
            ? <><ArrowsClockwise size={12} className="animate-spin" /> strategic partner cross-referencing…</>
            : <><Brain weight="duotone" size={14} /> save & cross-reference</>}
        </button>
        <span className="text-[10px] font-mono text-slate-500">
          On save · Strategic Partner pulls FRED / BLS / GDELT · returns Shared Victory verdict.
        </span>
      </div>
    </form>
  );
}

/* ─── Fused Retrospective viewer (split pane: human ↔ Strategic Partner) ───────────── */
function FusedRetrospective({ retro }) {
  const v = retro.validation || {};
  const tone = ALIGNMENT_TONE[v.alignment] || ALIGNMENT_TONE.PARTIAL;

  return (
    <div className="p-5 space-y-4" data-testid={`retro-detail-${retro.id}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base font-semibold text-slate-50">{retro.title}</h3>
          <p className="text-[11px] text-slate-500 font-mono mt-0.5">
            {retro.author} · {new Date(retro.created_at).toLocaleString()}
            {retro.scope?.label && <> · {retro.scope.label}</>}
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border verified-pulse
                        ${tone.border} ${tone.bg}`}
             data-testid="retro-alignment-seal">
          {v.alignment === "CORROBORATED"
            ? <ShieldCheck weight="fill" className={tone.fg} size={14} />
            : <ShieldWarning weight="fill" className={tone.fg} size={14} />}
          <div className="leading-tight">
            <div className={`tracking-mono-label text-[10px] ${tone.fg}`}>
              {v.alignment || "PARTIAL"} · {Math.round((v.confidence || 0) * 100)}% confidence
            </div>
            <div className="font-mono text-[10px] text-emerald-300/80">
              shared victory · Co-Authored Outcome
            </div>
          </div>
        </div>
      </div>

      {/* Strategic Partner verdict band */}
      <div className={`rounded-sm border p-3 ${tone.border} ${tone.bg}`}>
        <div className="flex items-center gap-2 mb-1">
          <Brain weight="duotone" className="text-fuchsia-400" size={14} />
          <span className="tracking-mono-label text-[10px] text-fuchsia-200">Strategic Partner verdict</span>
        </div>
        <p className="text-sm text-slate-100 leading-snug" data-testid="retro-verdict">{v.verdict || "—"}</p>
      </div>

      {/* SPLIT PANE · Professional ↔ Agentic */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-800">
        {/* LEFT · Professional input */}
        <div className="bg-slate-950 p-5" data-testid="retro-left-pane">
          <div className="tracking-mono-label text-[10px] text-emerald-300 mb-2 flex items-center gap-2">
            <Quotes weight="duotone" size={12} /> Professional Input
          </div>
          <NoteBlock label="Strategy effectiveness" value={retro.notes?.strategy_effectiveness} />
          <NoteBlock label="Supplier behavior"      value={retro.notes?.supplier_behavior} />
          <NoteBlock label="Market correction"      value={retro.notes?.market_correction} />
        </div>

        {/* RIGHT · Agentic validation */}
        <div className="bg-slate-950 p-5 space-y-3" data-testid="retro-right-pane">
          <div className="tracking-mono-label text-[10px] text-fuchsia-300 mb-1 flex items-center gap-2">
            <Globe weight="duotone" size={12} /> Agentic Validation · FRED · BLS · GDELT
          </div>

          <ul className="space-y-2" data-testid="retro-xrefs">
            {(v.cross_references || []).map((x, i) => (
              <li key={i} data-testid={`retro-xref-${i}`}
                  className={`p-2.5 rounded-sm border ${x.supports ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className={`font-mono text-[10px] tracking-mono-label ${SOURCE_FG[x.source] || "text-slate-300"}`}>
                    {x.source} · {x.code}
                  </span>
                  <span className={`font-mono text-[10px] ${x.supports ? "text-emerald-300" : "text-rose-300"}`}>
                    {x.supports ? "✓ supports" : "✕ counter"}
                  </span>
                </div>
                <div className="text-[11px] text-slate-100 leading-snug">{x.label}</div>
                <div className="text-[11px] text-slate-300 leading-snug mt-0.5">{x.observation}</div>
                {x.note && <p className="text-[10px] text-slate-400 mt-1 italic leading-snug">{x.note}</p>}
              </li>
            ))}
            {(v.cross_references || []).length === 0 && (
              <li className="text-[11px] font-mono text-slate-500 text-center py-3">No cross-references in this retro yet.</li>
            )}
          </ul>

          {(v.counter_evidence || []).length > 0 && (
            <div className="border-l-2 border-amber-500/40 pl-3 py-1">
              <div className="tracking-mono-label text-[10px] text-amber-300 mb-1">Counter-evidence</div>
              <ul className="text-[11px] text-slate-300 space-y-1 leading-snug">
                {v.counter_evidence.map((c, i) => <li key={i}>· {c}</li>)}
              </ul>
            </div>
          )}

          {v.institutional_insight && (
            <div className="border-l-2 border-fuchsia-500/40 pl-3 py-1">
              <div className="tracking-mono-label text-[10px] text-fuchsia-300 mb-1 flex items-center gap-1">
                <GraduationCap weight="duotone" size={11} /> Institutional Insight (durable)
              </div>
              <p className="text-xs text-slate-100 leading-snug" data-testid="retro-insight">
                {v.institutional_insight}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NoteBlock({ label, value }) {
  if (!value) return null;
  return (
    <div className="border-l-2 border-emerald-500/40 pl-3 py-1 mb-3">
      <div className="tracking-mono-label text-[10px] text-emerald-300">{label}</div>
      <p className="text-xs text-slate-100 leading-snug mt-0.5 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

/* ─── Atoms ─────────────────────────────────────────────────────────────── */
function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <input
        data-testid={testid}
        type="text" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-500/60 outline-none"
      />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <textarea
        data-testid={testid}
        rows={rows} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 placeholder:text-slate-600 focus:border-emerald-500/60 outline-none"
      />
    </label>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="tracking-mono-label text-[10px] text-slate-500">{label}</span>
      <select
        data-testid={testid}
        value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full bg-slate-950 border border-slate-800 rounded-sm px-2.5 py-1.5
                   text-sm text-slate-100 focus:border-emerald-500/60 outline-none"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-10 text-center" data-testid="retro-empty">
      <ChatTeardropDots weight="duotone" className="text-emerald-400/40" size={48} />
      <div className="font-display text-base text-slate-200 mt-3">
        Capture what your professional sense saw before the market did.
      </div>
      <p className="text-xs text-slate-400 font-mono max-w-md mt-2 leading-relaxed">
        Strategy effectiveness · supplier behavior · market correction.
        Strategic Partner will cross-reference the prose against FRED · BLS · GDELT and lock the
        durable insight into the Shared Victory Ledger.
      </p>
      <button onClick={onAdd}
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10
                   text-emerald-200 font-mono text-xs tracking-mono-label hover:bg-emerald-500/20">
        <Plus weight="bold" size={12} /> add retrospective
      </button>
    </div>
  );
}
